import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import emailjs from '@emailjs/browser';
import './Contact.css';

const EMAILJS_SERVICE_ID  = 'service_q35t12j';
const EMAILJS_TEMPLATE_ID = 'template_m0nq2hv';
const EMAILJS_PUBLIC_KEY  = 'PjubIOXdCrParLVPc';

const Contact = ({ navigateTo }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        message: '',
        attachments: []
    });
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState('success');
    const [alertMessage, setAlertMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const formRef = useRef(null);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    const projectTypes = [
        'Résidentiel',
        'Commercial',
        'Industriel',
        'Infrastructure',
        'Institutionnel',
        'Sports et Loisirs',
        'Autre'
    ];

    const contactInfo = {
        address: '617-350 boul. de la Côte-Vertu, Saint-Laurent, Montréal, QC',
        phone: '+1 438 994-6843',
        email: 'info@dhrstructures.ca',
        hours: {
            weekdays: 'Lundi - Vendredi: 8h00 - 17h00',
            weekend: 'Samedi: 9h00 - 12h00',
            closed: 'Dimanche: Fermé'
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prevState => ({
            ...prevState,
            attachments: [...prevState.attachments, ...files]
        }));
    };

    const removeAttachment = (index) => {
        setFormData(prevState => ({
            ...prevState,
            attachments: prevState.attachments.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSending(true);

        try {
            const pieces_jointes = formData.attachments.length > 0
                ? formData.attachments.map(f => `• ${f.name} (${(f.size / 1024).toFixed(1)} KB)`).join('\n')
                : 'Aucune pièce jointe';

            await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    firstName:      formData.firstName,
                    lastName:       formData.lastName,
                    email:          formData.email,
                    phone:          formData.phone       || 'Non fourni',
                    company:        formData.company     || 'Non fourni',
                    projectType:    formData.projectType || 'Non spécifié',
                    message:        formData.message,
                    pieces_jointes: pieces_jointes,
                },
                EMAILJS_PUBLIC_KEY
            );
            setAlertType('success');
            setAlertMessage('Votre message a été envoyé avec succès ! Nous vous contacterons sous 24 heures. Si vous avez des fichiers à joindre, répondez à l’email de confirmation ou envoyez-les directement à info@dhrstructures.ca');
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                company: '',
                projectType: '',
                message: '',
                attachments: []
            });
        } catch (error) {
            console.error('EmailJS error:', error);
            setAlertType('danger');
            setAlertMessage("Une erreur s'est produite lors de l'envoi. Veuillez réessayer ou nous contacter directement à info@dhrstructures.ca");
        } finally {
            setIsSending(false);
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 8000);
        }
    };

    return (
        <section className="contact-section" ref={ref} style={{ paddingTop: '120px' }}>
            <Container>
                {/* Header Section */}
                <motion.div
                    className="contact-header"
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8 }}
                >
                    <div className="page-eyebrow">
                        <span className="page-eyebrow-line"></span>
                        <span className="page-eyebrow-text">DHR Structures | Contactez-nous</span>
                        <span className="page-eyebrow-dot"></span>
                    </div>
                    <h1 className="contact-title">
                        Démarrons Votre Projet<br />
                        <span className="highlight-text">Ensemble</span>
                    </h1>
                    <p className="contact-subtitle">
                        Nos experts sont à votre disposition pour discuter de vos besoins en ingénierie structurale
                        et vous proposer des solutions sur mesure.
                    </p>
                </motion.div>

                {/* Contact Form and Info */}
                <Row className="contact-main">
                    <Col lg={8} className="mb-4">
                        <motion.div
                            className="contact-form-container"
                            initial={{ opacity: 0, x: -40 }}
                            animate={isInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="form-header">
                                <h3>Formulaire de Contact</h3>
                                <p>Remplissez ce formulaire et nous vous contacterons rapidement</p>
                            </div>

                            {showAlert && (
                                <Alert variant={alertType} className="custom-alert">
                                    {alertMessage}
                                </Alert>
                            )}

                            <Form ref={formRef} onSubmit={handleSubmit} className="contact-form">
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Prénom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleInputChange}
                                                required
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleInputChange}
                                                required
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Téléphone</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Entreprise</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Type de Projet</Form.Label>
                                            <Form.Select
                                                name="projectType"
                                                value={formData.projectType}
                                                onChange={handleInputChange}
                                                className="custom-input"
                                            >
                                                <option value="">Sélectionnez un type</option>
                                                {projectTypes.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Message *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="message"
                                        value={formData.message}
                                        onChange={handleInputChange}
                                        required
                                        className="custom-input"
                                        placeholder="Décrivez votre projet et vos besoins..."
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Pièces Jointes</Form.Label>
                                    <div className="file-upload-area">
                                        <Form.Control
                                            type="file"
                                            multiple
                                            onChange={handleFileUpload}
                                            className="file-input"
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.dwg"
                                        />
                                        <div className="upload-info">
                                            <i className="bi bi-cloud-upload"></i>
                                            <span>Cliquez pour sélectionner vos fichiers</span>
                                            <small>Formats acceptés : PDF, DOC, JPG, PNG, DWG (Max 10 MB)</small>
                                        </div>
                                    </div>
                                    <small className="text-muted d-block mt-2">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Les noms de fichiers seront inclus dans l’email. Pour envoyer les fichiers réels, joignez-les directement à <a href="mailto:info@dhrstructures.ca">info@dhrstructures.ca</a>.
                                    </small>

                                    {formData.attachments.length > 0 && (
                                        <div className="attachments-list">
                                            {formData.attachments.map((file, index) => (
                                                <div key={index} className="attachment-item">
                                                    <i className="bi bi-file-earmark"></i>
                                                    <span>{file.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAttachment(index)}
                                                        className="remove-attachment"
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Form.Group>

                                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                                    <Button type="submit" className="submit-btn" disabled={isSending}>
                                        {isSending
                                            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Envoi en cours...</>
                                            : <><i className="bi bi-send me-2"></i>Envoyer le Message</>
                                        }
                                    </Button>
                                </motion.div>
                            </Form>
                        </motion.div>
                    </Col>

                    <Col lg={4}>
                        <motion.div
                            className="contact-info"
                            initial={{ opacity: 0, x: 40 }}
                            animate={isInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className="info-card">
                                <h4>Informations de Contact</h4>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <i className="bi bi-geo-alt-fill"></i>
                                    </div>
                                    <div className="info-content">
                                        <strong>Adresse</strong>
                                        <p>{contactInfo.address}</p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <i className="bi bi-telephone-fill"></i>
                                    </div>
                                    <div className="info-content">
                                        <strong>Téléphone</strong>
                                        <p>{contactInfo.phone}</p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <i className="bi bi-envelope-fill"></i>
                                    </div>
                                    <div className="info-content">
                                        <strong>Email</strong>
                                        <p>{contactInfo.email}</p>
                                    </div>
                                </div>

                                <div className="info-item">
                                    <div className="info-icon">
                                        <i className="bi bi-clock-fill"></i>
                                    </div>
                                    <div className="info-content">
                                        <strong>Heures d'Ouverture</strong>
                                        <p>{contactInfo.hours.weekdays}</p>
                                        <p>{contactInfo.hours.weekend}</p>
                                        <p>{contactInfo.hours.closed}</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </Col>
                </Row>

                {/* Google Maps Integration */}
                <motion.div
                    className="map-section"
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.6 }}
                >
                    <h3>Notre Localisation</h3>
                    <div className="map-container">
                        <iframe
                            src="https://maps.google.com/maps?q=350+boul.+de+la+C%C3%B4te-Vertu+Saint-Laurent+Montr%C3%A9al+QC+H4N+1E3&output=embed&z=15"
                            width="100%"
                            height="400"
                            style={{ border: 0, borderRadius: '15px' }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="DHR Structures Location"
                        ></iframe>
                    </div>
                </motion.div>



            </Container>
        </section>
    );
};

export default Contact;